import React, { useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { hasMerchantPermission } from "../../api/merchantAuthApi";
import {
  getOutletProductSettings,
  compressImageFile,
  updateOutletSettings,
} from "../../api/outletProductSettingApi";
import ModuleHeader, { ModuleHeaderAction } from "../../components/ModuleHeader/ModuleHeader";
import ModulePageShell from "../../components/ModulePageShell/ModulePageShell";
import "./outlet-product.scss";

const emptyOutlet = {
  name: "", logo: "", cover_image: "", phone: "", email: "", accepting_orders: false,
  address: "", google_map_url: "", bank_name: "", bank_receiver_name: "", bank_account_number: "",
};

function ReadOnlyField({ label, value, note }) {
  return <label className="outlet-product__field outlet-product__field--readonly"><span>{label}</span><input value={value || "—"} readOnly />{note ? <small>{note}</small> : null}</label>;
}

function ImageField({ label, size, value, onChange, disabled }) {
  return (
    <div className={`outlet-product__image-upload outlet-product__image-upload--${size}`}>
      <span>{label}</span>
      <label className={`outlet-product__image-upload-box ${value ? "has-preview" : ""}`}>
        {value ? <img className="outlet-product__image-preview" src={value} alt={`${label} preview`} /> : <div className="outlet-product__image-placeholder"><strong>Upload Image</strong><small>PNG or JPG, maximum 5 MB.</small></div>}
        <input type="file" disabled={disabled} accept="image/png,image/jpeg,image/webp" onChange={onChange} />
      </label>
      {value && !disabled ? <button className="outlet-product__image-remove" type="button" onClick={() => onChange(null)}>Remove image</button> : null}
    </div>
  );
}

const money = (value) => value === null || value === undefined ? "—" : `RM${Number(value).toFixed(2)}`;
const timeRange = (start, end) => start && end ? `${start} - ${end}` : "—";

export default function OutletProductPage() {
  const navigate = useNavigate();
  const { selectedOutlet } = useOutletContext();
  const [outlet, setOutlet] = useState(emptyOutlet);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const canUpdateOutlet = hasMerchantPermission("outlet_update");
  const canUpdateProduct = hasMerchantPermission("product_update");

  useEffect(() => {
    if (!selectedOutlet?.id) return;
    setLoading(true);
    setError("");
    setMessage("");
    getOutletProductSettings(selectedOutlet.id)
      .then((data) => {
        setOutlet({ ...emptyOutlet, ...data.outlet });
        setProduct(data.product);
      })
      .catch((requestError) => setError(requestError.message))
      .finally(() => setLoading(false));
  }, [selectedOutlet?.id]);

  const updateField = (field, value) => {
    setMessage("");
    setOutlet((current) => ({ ...current, [field]: value }));
  };

  const handleImage = async (field, eventOrNull) => {
    if (!eventOrNull) return updateField(field, "");
    const file = eventOrNull.target.files?.[0];
    eventOrNull.target.value = "";
    if (!file) return;
    try {
      const options = field === "logo"
        ? { maxWidth: 700, maxHeight: 700, maxBytes: 300 * 1024, quality: 0.86 }
        : { maxWidth: 1920, maxHeight: 640, maxBytes: 600 * 1024, quality: 0.84 };
      updateField(field, await compressImageFile(file, options));
    } catch (imageError) {
      setError(imageError.message);
    }
  };

  const saveOutlet = async () => {
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const data = await updateOutletSettings(selectedOutlet.id, {
        logo: outlet.logo || null,
        cover_image: outlet.cover_image || null,
        phone: outlet.phone || null,
        email: outlet.email || null,
        accepting_orders: Boolean(outlet.accepting_orders),
        address: outlet.address,
        google_map_url: outlet.google_map_url || null,
      });
      setOutlet({ ...emptyOutlet, ...data.outlet });
      setProduct(data.product);
      setMessage("Outlet settings saved successfully.");
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModulePageShell className="outlet-product">
      <ModuleHeader
        bleed
        title="Outlet / Product Setup"
        description="Manage outlet profile and product listing settings."
        actions={
          canUpdateOutlet ? (
            <ModuleHeaderAction disabled={saving || loading} onClick={saveOutlet}>
              {saving ? "Saving..." : "Save"}
            </ModuleHeaderAction>
          ) : null
        }
      />

      {error ? <div className="outlet-product__feedback outlet-product__feedback--error">{error}</div> : null}
      {message ? <div className="outlet-product__feedback outlet-product__feedback--success">{message}</div> : null}

      <section className="outlet-product__card">
        <div className="outlet-product__card-header"><div><h3>Outlet Setup</h3><p>Merchant can only update operational outlet details. Business and bank information are managed by admin.</p></div></div>
        {loading ? <p>Loading outlet settings...</p> : (
          <>
            <div className="outlet-product__section">
              <div className="outlet-product__section-title"><h4>Outlet Information</h4></div>
              <div className="outlet-product__media-grid">
                <ImageField disabled={!canUpdateOutlet} label="Outlet Logo" size="logo" value={outlet.logo} onChange={(event) => handleImage("logo", event)} />
                <ImageField disabled={!canUpdateOutlet} label="Outlet Cover Image" size="cover" value={outlet.cover_image} onChange={(event) => handleImage("cover_image", event)} />
              </div>
              <div className="outlet-product__form-grid outlet-product__form-grid--three">
                <ReadOnlyField label="Outlet Name" value={outlet.name} note="Managed by admin." />
                <label className="outlet-product__field"><span>Phone</span><input disabled={!canUpdateOutlet} value={outlet.phone || ""} onChange={(event) => updateField("phone", event.target.value)} /></label>
                <label className="outlet-product__field"><span>Operational Email</span><input type="email" disabled={!canUpdateOutlet} value={outlet.email || ""} onChange={(event) => updateField("email", event.target.value)} placeholder="outlet@example.com" /></label>
                <label className="outlet-product__field"><span>Accepting Orders</span><select disabled={!canUpdateOutlet} value={outlet.accepting_orders ? "1" : "0"} onChange={(event) => updateField("accepting_orders", event.target.value === "1")}><option value="1">Yes</option><option value="0">No</option></select></label>
                <label className="outlet-product__field"><span>Address</span><input disabled={!canUpdateOutlet} value={outlet.address || ""} onChange={(event) => updateField("address", event.target.value)} /></label>
                <label className="outlet-product__field"><span>Google Maps URL</span><input disabled={!canUpdateOutlet} value={outlet.google_map_url || ""} onChange={(event) => updateField("google_map_url", event.target.value)} /></label>
              </div>
            </div>
            <div className="outlet-product__section">
              <div className="outlet-product__section-title"><h4>Bank Information</h4><p>Managed by platform admin.</p></div>
              <div className="outlet-product__form-grid outlet-product__form-grid--three">
                <ReadOnlyField label="Bank Name" value={outlet.bank_name} />
                <ReadOnlyField label="Receiver Name" value={outlet.bank_receiver_name} />
                <ReadOnlyField label="Account Number" value={outlet.bank_account_number} />
              </div>
            </div>
          </>
        )}
      </section>

      <section className="outlet-product__card">
        <div className="outlet-product__card-header"><div><h3>Product Setup</h3><p>For MVP, each outlet manages one product listing. Daily quantity and availability are managed under Today’s Availability.</p></div></div>
        <div className="outlet-product__table outlet-product__table--products">
          <div className="outlet-product__table-head"><span>Product</span><span>Price</span><span>Original Price</span><span>Pickup Window</span><span>Available End</span><span>Actions</span></div>
          {product ? (
            <div className="outlet-product__table-row">
              <span>{product.name || "Unnamed product"}</span><span>{money(product.price)}</span><span>{money(product.normal_price)}</span><span>{timeRange(product.pickup_start_time, product.pickup_end_time)}</span><span>{product.available_end_time || "—"}</span>
              {canUpdateProduct ? <button type="button" onClick={() => navigate(`/products/${product.id}/edit`)}>Edit</button> : <span>View only</span>}
            </div>
          ) : <div className="outlet-product__feedback">No product has been configured for this outlet.</div>}
        </div>
      </section>
    </ModulePageShell>
  );
}
